import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { getAppEntryPath, storePendingInvitation } from '../utils/userCompany';
import { PRICING_PLANS, formatPlanPrice } from '../config/pricingPlans';
import { FAQ_ITEMS, buildStructuredData } from '../config/siteSeo';
import SiteFooter from '../components/SiteFooter';
import './Landing.css';

const STRUCTURED_DATA = buildStructuredData();

const FEATURES = [
  {
    icon: 'task_alt',
    title: 'Streamlined Workflow',
    body: 'Assign tasks, track progress, and manage your entire service operation from one centralized platform. No more juggling multiple tools or missing deadlines.'
  },
  {
    icon: 'groups',
    title: 'Team Coordination',
    body: 'Keep your entire team in sync with real-time updates, location-based task assignments, and seamless communication. Everyone knows what needs to be done and where.'
  },
  {
    icon: 'checklist',
    title: 'Quality Assurance',
    body: 'Built-in checklists, photo documentation, and detailed task logs ensure nothing falls through the cracks. Maintain consistent service quality and reduce costly mistakes.'
  }
];

const Landing = () => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    website: ''
  });
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (loading) {
      return;
    }

    const invitation = searchParams.get('invitation');
    if (invitation) {
      storePendingInvitation(invitation);
      axios.get(`/api/companies/invitations/token/${invitation}`)
        .then((response) => {
          if (user) {
            navigate(`/accept-invitation?invitation=${invitation}`);
            return;
          }
          if (response.data.data.userExists) {
            navigate(`/login?invitation=${invitation}`);
          } else {
            navigate(`/register?invitation=${invitation}`);
          }
        })
        .catch(() => {
          navigate('/login');
        });
    }
  }, [user, loading, navigate, searchParams]);

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
    setContactError('');
    setContactSuccess('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactSuccess('');
    setIsSubmitting(true);

    try {
      await axios.post('/api/contact', contactForm);
      setContactSuccess('Thank you, we will get in touch as soon as possible');
      setContactForm({ name: '', email: '', message: '', website: '' });
    } catch (error) {
      setContactError(formatError(error) || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const logoPath = '/service_in_mind_logo_white.png';

  let heroCta = null;
  if (!loading && user) {
    heroCta = (
      <button
        type="button"
        className="btn btn-primary landing-cta-button"
        onClick={() => navigate(getAppEntryPath(user))}
      >
        Open app
      </button>
    );
  } else if (!loading) {
    heroCta = (
      <button
        type="button"
        className="btn btn-primary landing-cta-button"
        onClick={() => navigate('/register')}
      >
        Create account
      </button>
    );
  }

  return (
    <div className="landing-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />
      <main>
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-hero-copy">
            <div className="landing-logo-container">
              <img
                src={logoPath}
                alt="Service in Mind"
                className="landing-logo"
              />
            </div>

            <h1 className="landing-headline">
              Save time. Reduce mistakes. Stay in control.
            </h1>

            <p className="landing-description">
              Service in Mind is the task management app built for service professionals—organize tasks, teams, and locations with ease.
            </p>

            <div className="landing-cta-section">
              {heroCta}
            </div>

            {!loading && !user && (
              <p className="landing-login-prompt">
                Already have an account?{' '}
                <button
                  type="button"
                  className="landing-login-link"
                  onClick={() => navigate('/login')}
                >
                  Log in
                </button>
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="landing-features" aria-labelledby="landing-features-heading">
        <h2 id="landing-features-heading" className="landing-features-heading">Why Service in Mind</h2>
        <div className="landing-features-inner">
          {FEATURES.map((feature, index) => (
            <article
              key={feature.title}
              className={`landing-feature-row${index % 2 === 1 ? ' landing-feature-row--reverse' : ''}`}
            >
              <div className="landing-feature-content">
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </div>
              <div className="landing-feature-accent" aria-hidden="true">
                <span className="landing-feature-icon">
                  <span className="material-symbols-outlined">{feature.icon}</span>
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="pricing" className="landing-pricing" aria-labelledby="landing-pricing-heading">
        <h2 id="landing-pricing-heading" className="landing-pricing-heading">
          Pricing
        </h2>
        <div className="landing-pricing-grid">
          {PRICING_PLANS.map((plan) => (
            <article
              key={plan.id}
              className={`landing-pricing-card${plan.featured ? ' landing-pricing-card--featured' : ''}`}
              aria-labelledby={`plan-${plan.id}-name`}
            >
              {plan.badge && (
                <span className="landing-pricing-badge">{plan.badge}</span>
              )}
              <div className="landing-pricing-card-header">
                <h3 id={`plan-${plan.id}-name`} className="landing-pricing-plan-name">
                  {plan.name}
                </h3>
                <p className="landing-pricing-plan-price">{formatPlanPrice(plan)}</p>
                {plan.trialDays ? (
                  <p className="landing-pricing-trial-note">
                    {plan.trialDays}-day free trial — card required
                  </p>
                ) : null}
              </div>
              <p className="landing-pricing-plan-description">{plan.description}</p>
              <ul className="landing-pricing-features" aria-label={`${plan.name} features`}>
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn-primary landing-pricing-cta"
                onClick={scrollToContact}
                aria-label={`Get in touch about the ${plan.name} plan`}
              >
                Get in touch
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-faq" aria-labelledby="landing-faq-heading">
        <h2 id="landing-faq-heading" className="landing-faq-heading">
          Frequently asked questions
        </h2>
        <div className="landing-faq-list">
          {FAQ_ITEMS.map((item) => (
            <article key={item.question} className="landing-faq-item">
              <h3 className="landing-faq-question">{item.question}</h3>
              <p className="landing-faq-answer">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="contact" className="landing-contact-section" aria-labelledby="landing-contact-heading">
        <div className="landing-contact-inner">
          <div className="landing-contact">
            <h2 id="landing-contact-heading" className="landing-contact-title">Contact</h2>
            <form className="landing-contact-form" onSubmit={handleContactSubmit}>
              <div className="form-group">
                <label htmlFor="contact-name">Name</label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={contactForm.name}
                  onChange={handleContactChange}
                  maxLength={100}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">Email</label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  rows="5"
                  maxLength={2000}
                  required
                />
              </div>
              <div className="landing-contact-honeypot" aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  type="text"
                  id="contact-website"
                  name="website"
                  value={contactForm.website}
                  onChange={handleContactChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              {contactError && <div className="error" role="alert">{contactError}</div>}
              {contactSuccess && <div className="success" role="status">{contactSuccess}</div>}
              <button
                type="submit"
                className="btn btn-primary landing-contact-submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
      </main>

      <SiteFooter className="landing-footer" />
    </div>
  );
};

export default Landing;
